import { useEffect, useState, useMemo } from 'react'
import { Plus, LayoutTemplate, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { TemplateListRow } from '@/components/templates/TemplateListRow'
import { TemplateFormDialog } from '@/components/templates/TemplateFormDialog'
import { TemplatePreviewDialog } from '@/components/templates/TemplatePreviewDialog'
import { useOutlineTemplatesStore } from '@/stores/outline-templates-store'
import { useAuthStore } from '@/stores/auth-store'
import { toast } from 'sonner'
import type { OutlineTemplate } from '@/lib/api'

// ===================
// Types
// ===================

type FilterTab = 'all' | 'my' | 'shared'

// ===================
// Page Component
// ===================

export function TemplatesPage() {
  const { templates, isLoading, error, fetchTemplates, createTemplate, updateTemplate, deleteTemplate, duplicateTemplate } =
    useOutlineTemplatesStore()
  const user = useAuthStore((s) => s.user)
  const role = useAuthStore((s) => s.role)

  const currentUserId = user?.id ?? ''
  const isAdmin = role === 'admin'

  const [filter, setFilter] = useState<FilterTab>('all')

  // Dialog state
  const [formOpen, setFormOpen] = useState(false)
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create')
  const [editingTemplate, setEditingTemplate] = useState<OutlineTemplate | null>(null)
  const [previewTemplate, setPreviewTemplate] = useState<OutlineTemplate | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<OutlineTemplate | null>(null)

  useEffect(() => {
    void fetchTemplates()
  }, [fetchTemplates])

  // Filter templates client-side
  const filteredTemplates = useMemo(() => {
    switch (filter) {
      case 'my':
        return templates.filter((t) => t.createdBy === currentUserId)
      case 'shared':
        return templates.filter((t) => t.visibility === 'shared')
      default:
        return templates
    }
  }, [templates, filter, currentUserId])

  // Actions
  const handleCreate = () => {
    setEditingTemplate(null)
    setFormMode('create')
    setFormOpen(true)
  }

  const handleEdit = (template: OutlineTemplate) => {
    setEditingTemplate(template)
    setFormMode('edit')
    setFormOpen(true)
  }

  const handlePreview = (template: OutlineTemplate) => {
    setPreviewTemplate(template)
  }

  const handleDuplicate = async (template: OutlineTemplate) => {
    try {
      await duplicateTemplate(template.id)
      toast.success('Template duplicated')
    } catch {
      toast.error('Failed to duplicate template')
    }
  }

  const handleToggleVisibility = async (template: OutlineTemplate) => {
    const newVisibility = template.visibility === 'private' ? 'shared' : 'private'
    try {
      await updateTemplate(template.id, { visibility: newVisibility })
      toast.success(newVisibility === 'shared' ? 'Template shared with organisation' : 'Template set to private')
    } catch {
      toast.error('Failed to update visibility')
    }
  }

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return
    try {
      await deleteTemplate(deleteTarget.id)
      toast.success('Template deleted')
    } catch {
      toast.error('Failed to delete template')
    } finally {
      setDeleteTarget(null)
    }
  }

  const handleFormSave = async (data: {
    name: string
    description?: string
    sections: { title: string; description?: string; targetWordCount?: number }[]
    visibility: 'private' | 'shared'
  }) => {
    if (formMode === 'edit' && editingTemplate) {
      await updateTemplate(editingTemplate.id, data)
      toast.success('Template updated')
    } else {
      await createTemplate(data)
      toast.success('Template created')
    }
  }

  // Shared action props for rows
  const actionProps = {
    currentUserId,
    isAdmin,
    onPreview: handlePreview,
    onEdit: handleEdit,
    onDuplicate: handleDuplicate,
    onToggleVisibility: handleToggleVisibility,
    onDelete: (t: OutlineTemplate) => setDeleteTarget(t),
  }

  // Loading state
  if (isLoading && templates.length === 0) {
    return (
      <div className="flex flex-col h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground mt-2">Loading templates...</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="bg-background px-6 py-3">
        <div className="flex items-center justify-between">
          {/* Filter tabs */}
          <Tabs value={filter} onValueChange={(v) => setFilter(v as FilterTab)}>
            <TabsList className="bg-muted p-1 h-9 rounded-lg">
              <TabsTrigger value="all" className="h-7 px-3 rounded-md data-[state=active]:bg-card data-[state=active]:shadow-sm">All</TabsTrigger>
              <TabsTrigger value="my" className="h-7 px-3 rounded-md data-[state=active]:bg-card data-[state=active]:shadow-sm">My Templates</TabsTrigger>
              <TabsTrigger value="shared" className="h-7 px-3 rounded-md data-[state=active]:bg-card data-[state=active]:shadow-sm">Shared</TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Actions */}
          <Button size="sm" className="gap-1.5" onClick={handleCreate}>
            <Plus className="h-4 w-4" />
            New Template
          </Button>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="bg-destructive/10 border-b border-destructive/20 px-6 py-2 flex items-center justify-between">
          <p className="text-sm text-destructive">{error}</p>
          <Button variant="ghost" size="sm" onClick={() => void fetchTemplates(true)}>Try again</Button>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {filteredTemplates.length === 0 ? (
          <EmptyState filter={filter} onCreateClick={handleCreate} />
        ) : (
          <div className="overflow-x-auto rounded-lg border bg-card shadow-sm">
            <table className="w-full">
              <thead>
                <tr className="bg-muted/50 border-b">
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Name</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground w-24">Sections</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground w-20">Visibility</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground w-28">Creator</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-muted-foreground w-20">Updated</th>
                  <th className="px-4 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {filteredTemplates.map((template) => (
                  <TemplateListRow key={template.id} template={template} {...actionProps} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Dialogs */}
      <TemplateFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        template={editingTemplate}
        onSave={handleFormSave}
        mode={formMode}
      />

      <TemplatePreviewDialog
        open={!!previewTemplate}
        onOpenChange={(open) => !open && setPreviewTemplate(null)}
        template={previewTemplate}
        currentUserId={currentUserId}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete &ldquo;{deleteTarget?.name}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              This can&apos;t be undone. Outlines already created from this template won&apos;t be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void handleDeleteConfirm()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

// ===================
// Empty State
// ===================

function EmptyState({ filter, onCreateClick }: { filter: FilterTab; onCreateClick: () => void }) {
  if (filter === 'my') {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <LayoutTemplate className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
        <h3 className="text-base font-medium mb-1">No templates yet</h3>
        <p className="text-sm text-muted-foreground mb-4 max-w-sm">
          You haven&apos;t created any templates yet.
        </p>
        <Button onClick={onCreateClick}>
          <Plus className="h-4 w-4 mr-2" />
          New Template
        </Button>
      </div>
    )
  }

  if (filter === 'shared') {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <LayoutTemplate className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
        <h3 className="text-base font-medium mb-1">No shared templates</h3>
        <p className="text-sm text-muted-foreground mb-4 max-w-sm">
          No shared templates in your organisation yet.
        </p>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <LayoutTemplate className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
      <h3 className="text-base font-medium mb-1">No templates yet</h3>
      <p className="text-sm text-muted-foreground mb-4 max-w-sm">
        Templates define reusable outline structures for common tender formats like RFPs, RFIs, and EOIs.
      </p>
      <Button onClick={onCreateClick}>
        <Plus className="h-4 w-4 mr-2" />
        New Template
      </Button>
    </div>
  )
}
